import React from 'react';
import { AlertCircle, Share2 } from 'lucide-react';
// 배럴(index.ts)이 아니라 순수 모듈에서 바로 가져온다.
// 배럴은 api.ts → ky(ESM)까지 끌고 와 이 컴포넌트를 단독으로 테스트할 수 없게 만든다.
import { resolveScheduleMembershipEmptyReason } from '../../features/sharedMembership/scheduleEntitlements';

type Props = {
    /** 목록에서 빠진 공유 수강권 수. 0이면 진짜로 수강권이 없는 회원이다. */
    excludedSharedCount?: number;
    onClickAddMembership: () => void;
    /** 카드 폭. 호출부마다 열 너비가 달라 그대로 맞춘다. */
    width?: number;
    style?: React.CSSProperties;
};

/**
 * "수강권이 하나도 안 뜬다"를 한 문장으로 뭉뚱그리지 않는다.
 * 공유 수강권이 이 강사에서 빠진 것뿐인데 "없는 회원"이라고 알리면
 * 직원은 같은 수강권을 중복 발급하거나 공유가 고장났다고 오인한다.
 *
 * 예전에는 '이 수업 방식에서는 공유를 못 쓴다'는 세 번째 안내가 있었고, 그 안내는
 * "공유 회차는 1:1 단일 수업에만 등록됩니다"라고 말했다. 서버가 1:2·반복·추가 일정에서도
 * 공유 회차를 받게 되면서 **그 문장이 거짓이 되었고 그 안내를 쓰는 화면도 없어져** 지웠다.
 */
const ScheduleMembershipEmptyNotice = ({
    excludedSharedCount = 0,
    onClickAddMembership,
    width = 383,
    style,
}: Props) => {
    const reason = resolveScheduleMembershipEmptyReason({ excludedSharedCount });

    const { title, body, isError } =
        reason.kind === 'NO_MEMBERSHIP'
            ? {
                  title: '등록된 수강권이 없는 회원이에요',
                  body: '수강권을 먼저 발급해야 일정을 만들 수 있습니다.',
                  isError: true,
              }
            : {
                  // 수강권은 분명히 있다. 없다고 말하면 직원이 같은 수강권을 또 발급한다.
                  title: `공유 수강권 ${reason.sharedCount}건은 담당 강사가 달라요`,
                  body: '공유 수강권은 그 수강권의 담당 강사 수업에만 등록할 수 있습니다. 담당 강사를 바꾸거나 개인 수강권을 사용해주세요.',
                  isError: false,
              };

    return (
        <div
            role="status"
            style={{
                width,
                minHeight: 140,
                marginTop: 12,
                padding: '18px 20px',
                borderRadius: 10,
                border: `1px solid ${isError ? '#FF9BA1' : '#BFD4F6'}`,
                backgroundColor: isError ? '#FFF7F7' : '#F5F8FF',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                ...style,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ color: isError ? '#E4525D' : '#2B529D', lineHeight: 0, marginTop: 2 }}>
                    {isError ? <AlertCircle size={18} /> : <Share2 size={18} />}
                </span>
                <div style={{ minWidth: 0 }}>
                    <p
                        style={{
                            margin: 0,
                            fontSize: 14,
                            fontWeight: 700,
                            color: '#313640',
                            lineHeight: '20px',
                        }}
                    >
                        {title}
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: '18px', color: '#5B6472' }}>
                        {body}
                    </p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 'auto', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    onClick={onClickAddMembership}
                    style={{
                        height: 34,
                        padding: '0 14px',
                        borderRadius: 8,
                        border: '1px solid #2B529D',
                        backgroundColor: '#2B529D',
                        color: '#FFFFFF',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: 'pointer',
                    }}
                >
                    수강권 추가하기
                </button>
            </div>
        </div>
    );
};

export default ScheduleMembershipEmptyNotice;
